/**
 * Document Category Hooks
 *
 * Wraps Convex category queries/mutations for managing document categories
 */

import { api } from "convex/_generated/api";
import { useWorkspaceId, useWorkspaceQuery } from "@/hooks";
import { useApiMutation } from "@/packages/features-shared";
import type { Id } from "@/packages/types";

/* ------------------------------------------------------------------ */
/* useDocumentCategories                                               */
/* ------------------------------------------------------------------ */

interface UseDocumentCategoriesReturn {
  categories: Array<{
    _id: Id<"documentCategories">;
    name: string;
    slug?: string;
    icon: string;
    isSystem: boolean;
    order: number;
  }>;
  isLoading: boolean;
}

export function useDocumentCategories(): UseDocumentCategoriesReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_category_queries.list,
    {},
  );

  return {
    categories: data ?? [],
    isLoading,
  };
}

/* ------------------------------------------------------------------ */
/* useCreateDocumentCategory                                           */
/* ------------------------------------------------------------------ */

export function useCreateDocumentCategory() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_category_mutations.createCategory,
    {
      name: "create-category",
      successMessage: "Category created",
    },
  );

  return (args: { name: string; icon: string; order?: number }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useUpdateDocumentCategory                                           */
/* ------------------------------------------------------------------ */

export function useUpdateDocumentCategory() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_category_mutations.updateCategory,
    {
      name: "update-category",
      successMessage: "Category updated",
    },
  );

  return (args: {
    id: Id<"documentCategories">;
    name?: string;
    icon?: string;
  }) => mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useDeleteDocumentCategory                                           */
/* ------------------------------------------------------------------ */

export function useDeleteDocumentCategory() {
  const workspaceId = useWorkspaceId();
  const mutation = useApiMutation(
    api.document_category_mutations.deleteCategory,
    {
      name: "delete-category",
      successMessage: "Category deleted",
    },
  );

  return (args: { id: Id<"documentCategories"> }) =>
    mutation.mutateAsync({ ...args, workspaceId });
}

/* ------------------------------------------------------------------ */
/* useCategoryDocumentCount                                            */
/* ------------------------------------------------------------------ */

interface UseCategoryDocumentCountReturn {
  count: number;
  isLoading: boolean;
}

export function useCategoryDocumentCount(
  categoryId: Id<"documentCategories"> | null,
): UseCategoryDocumentCountReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_category_queries.getDocumentCount,
    { categoryId },
  );

  return {
    count: data ?? 0,
    isLoading,
  };
}
